<script setup lang="ts">
// types
import type { BlockReviewStoryProps } from '@ts-blocks/block-review-story/interfaces'
// hooks
import { useBlockReviewStory } from '@hooks/blocks/block-review-story.hook'
// helpers
import { STRAPI_IMAGE_SIZES } from '@helpers/strapi-media'
// assets
import quoteMark from '@images/sections/review-stories/quote-mark.svg'
// components
import { AppImage } from '@UI/Images/AppImage'
import { Button, Icon } from '@brighterly/lib-lumee-ui'

defineOptions({
  name: 'BlockReviewStory',
})

const props = defineProps<{
  block: BlockReviewStoryProps
}>()

const {
  refImageAlt,
  refShowTutor,
  refShowCta,
  refCtaText,
  refCtaHref,
  handleClickCta,
} = useBlockReviewStory({
  block: () => props.block,
})
</script>

<template>
  <article
    class="block-review-story"
    :class="[
      `block-review-story--${block.surface ?? 'ultraviolet'}`,
      { 'block-review-story--left': block.imagePosition === 'left' },
    ]"
  >
    <div class="block-review-story__quote">
      <div class="block-review-story__quote-block">
        <img
          class="block-review-story__mark"
          :src="quoteMark"
          alt=""
          width="44"
          height="36"
        >
        <h3 class="block-review-story__title btly-text-heading-s">
          {{ block.title }}
        </h3>
        <p class="block-review-story__description btly-text-body-l">
          {{ block.description }}
        </p>
      </div>
      <div class="block-review-story__footer">
        <div class="block-review-story__meta">
          <div class="block-review-story__meta-row">
            <Icon
              name="graduation-cap"
              size="sm"
              weight="solid"
            />
            <span class="block-review-story__meta-label btly-text-title-m">Student:</span>
            <span class="block-review-story__meta-name btly-text-body-l">{{ block.studentName }}</span>
          </div>
          <div
            v-if="refShowTutor"
            class="block-review-story__meta-row"
          >
            <Icon
              name="book"
              size="sm"
              weight="solid"
            />
            <span class="block-review-story__meta-label btly-text-title-m">Tutor:</span>
            <span class="block-review-story__meta-name btly-text-body-l">{{ block.tutorName }}</span>
          </div>
        </div>
        <a
          v-if="refShowCta"
          class="block-review-story__cta"
          :href="refCtaHref"
          @click="handleClickCta"
        >
          <span class="block-review-story__cta-view block-review-story__cta-view--mobile">
            <Button
              view="secondary"
              size="md"
              :text="refCtaText"
              tabindex="-1"
            />
          </span>
          <span class="block-review-story__cta-view block-review-story__cta-view--tablet">
            <Button
              view="tertiary-accent"
              size="md"
              :text="refCtaText"
              tabindex="-1"
            />
          </span>
        </a>
      </div>
    </div>
    <div class="block-review-story__media">
      <div class="block-review-story__image-slot">
        <AppImage
          v-if="block.image"
          class="block-review-story__image"
          :media="block.image"
          :sizes="STRAPI_IMAGE_SIZES"
          :alt="refImageAlt"
        />
      </div>
    </div>
  </article>
</template>

<style scoped lang="scss" src="@scss/components/Blocks/BlockReviewStory.scss" />
